import { Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useSeo } from "@/hooks/useSeo";
import FaqSection from "@/components/feature/FaqSection";

const officeEntranceImg = "/images/GRANSTARK-OSAKA_office2_entrance.jpeg";
const officeCeoImg = "/images/GRANSTARK-OSAKA_office1_president's seat.jpeg";
const certificateImg = "/images/GRANSTARK-certificate(Osaka_PPSC).png";

const email = ['contact', 'granstark.co.jp'].join('@');

const companyData = [
  { label: "会社名", value: "株式会社GRANSTARK" },
  { label: "設立", value: "2019年10月10日" },
  { label: "代表者", value: "三田　晃士" },
  { label: "電話番号", value: "06-6786-9252", href: "tel:0667869252" },
  { label: "FAX番号", value: "06-6786-9253" },
  { label: "メールアドレス", value: email, href: 'mailto:' + email },
  { label: "資本金", value: "300万" },
  { label: "所在地", value: "大阪府大阪市浪速区難波中3-9-3 南朝日ビル804号" },
];

const licenseData = [
  { label: "認定をした公安委員会", value: "大阪府公安委員会" },
  { label: "認定の番号", value: "第62003216号" },
  { label: "有効期限", value: "令和5年3月23日〜令和10年3月22日" },
  { label: "氏名又は名称", value: "株式会社GRANSTARK" },
  { label: "所在地", value: "大阪府大阪市浪速区難波中3-9-3 南朝日ビル804号" },
];

const companyFaq = [
  {
    question: "GRANSTARKはどんな警備会社ですか？",
    answer: "株式会社GRANSTARKは2019年創業の大阪密着の警備会社です。大阪府公安委員会認定（第62003216号）を取得しており、交通誘導・高速道路・イベント（雑踏）・施設警備の4分野に対応しています。",
  },
  {
    question: "GRANSTARKに警備を依頼するにはどうすればいいですか？",
    answer: "お電話（06-6786-9252）またはホームページのお問い合わせフォームからご連絡ください。最短即日でご対応いたします。",
  },
  {
    question: "対応エリアはどこですか？",
    answer: "主に大阪府内全域に対応しています。",
  },
];

export default function Company() {
  useSeo({
    title: "会社概要｜株式会社GRANSTARK｜大阪・難波の警備会社",
    description:
      "株式会社GRANSTARKの会社概要。大阪府公安委員会認定の警備会社として、所在地・代表者・設立年月・事業内容・認定情報をご確認いただけます。",
    ogUrl: "https://www.gs-granstark.com/company",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": "https://www.gs-granstark.com/company#webpage",
        url: "https://www.gs-granstark.com/company",
        name: "会社概要｜株式会社GRANSTARK｜大阪・難波の警備会社",
        description:
          "株式会社GRANSTARKの会社概要。大阪府公安委員会認定の警備会社として、所在地・代表者・設立年月・事業内容・認定情報をご確認いただけます。",
        isPartOf: { "@id": "https://www.gs-granstark.com/#website" },
        publisher: { "@id": "https://www.gs-granstark.com/#business" },
        inLanguage: "ja",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: companyFaq.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      },
    ],
  });

  const { ref: ref1, isVisible: v1 } = useScrollReveal<HTMLDivElement>(0.15);
  const { ref: ref2, isVisible: v2 } = useScrollReveal<HTMLDivElement>(0.15, 150);
  const { ref: ref3, isVisible: v3 } = useScrollReveal<HTMLDivElement>(0.15, 300);

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[420px] md:h-[520px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat pointer-events-none"
          style={{ backgroundImage: `url('${officeEntranceImg}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/50 via-navy/60 to-navy/80 pointer-events-none" />
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center px-4">
            <span className="text-gold text-sm tracking-[4px] font-bold block mb-3">COMPANY</span>
            <h1 className="text-4xl md:text-5xl font-black text-white">会社概要</h1>
            <p className="text-gray-300 mt-4 text-lg">company</p>
          </div>
        </div>
      </section>

      {/* Greeting */}
      <section id="greeting" className="bg-white py-16 md:py-24">
        <div className="w-full px-4 md:px-8 max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-gold text-sm tracking-[4px] font-bold block mb-2">GREETING</span>
            <h2 className="text-2xl md:text-3xl font-bold text-navy">代表挨拶</h2>
            <div className="w-16 h-1 bg-gold mx-auto mt-4 rounded-full" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div className="h-80 md:h-[28rem] rounded-2xl overflow-hidden">
              <img
                src={officeCeoImg}
                alt="GRANSTARK代表・三田晃士のオフィス"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="space-y-4">
              <p className="text-gray-700 leading-loose">
                私たちグランシュタークは2023年3月23日に大阪府公安委員会より警備業認定を受けたベンチャー企業です。万博開催やIR誘致、リニアの開通など開発やイベントが目白押しの大阪において、地域住民や来場客の方々の安心・安全に寄与できることが大きな社会貢献であり、都市発展の一端を担えると考え参入いたしました。利他主義を経営理念に掲げる弊社は、地域住民の皆様、取引先様、従業員すべてのステークホルダーの皆様に安心・安全を提供し、恩恵・幸福を享受していただけるよう日々精進し、仕事に取り組みます。
              </p>
              <p className="text-right text-gray-600 mt-6 font-bold">
                代表取締役　三田　晃士
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section id="company-info" className="bg-gray-50 py-16 md:py-24">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-gold text-sm tracking-[4px] font-bold block mb-2">COMPANY</span>
            <h2 className="text-2xl md:text-3xl font-bold text-navy">会社概要</h2>
            <div className="w-16 h-1 bg-gold mx-auto mt-4 rounded-full" />
          </div>
          <div
            ref={ref1}
            className={`bg-white rounded-2xl overflow-hidden border border-gray-200 transition-all duration-700 ${
              v1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <table className="w-full text-sm">
              <tbody>
                {companyData.map((item, i) => (
                  <tr
                    key={item.label}
                    className={`${i % 2 === 0 ? "bg-white" : "bg-gray-50"} border-b border-gray-100 last:border-0`}
                  >
                    <th className="text-left py-4 px-6 md:px-8 font-bold text-navy w-32 md:w-40 shrink-0">
                      {item.label}
                    </th>
                    <td className="py-4 px-6 md:px-8 text-gray-700">
                      {item.href ? (
                        <a href={item.href} className="text-gold hover:underline">
                          {item.value}
                        </a>
                      ) : (
                        item.value
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* License */}
      <section id="license" className="bg-white py-16 md:py-24">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-[1200px] mx-auto">
          <div className="text-center mb-14">
            <span className="text-gold text-sm tracking-[4px] font-bold block mb-2">LICENSE</span>
            <h2 className="text-2xl md:text-3xl font-bold text-navy">警備業標識</h2>
            <div className="w-16 h-1 bg-gold mx-auto mt-4 rounded-full" />
          </div>
          <div
            ref={ref2}
            className={`transition-all duration-700 ${
              v2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <div className="space-y-6 w-full">
              <p className="text-sm text-gray-500">別記様式第2号（第6条関係）</p>
              <div className="border border-gray-300 rounded-lg overflow-hidden w-full">
                <table className="w-full text-sm md:text-base">
                  <tbody className="divide-y divide-gray-300">
                    {licenseData.map((item, i) => (
                      <tr
                        key={item.label}
                        className={`${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                      >
                        <th className="text-center py-5 px-4 md:px-8 font-bold text-navy w-1/2 border-r border-gray-300">
                          {item.label}
                        </th>
                        <td className="text-center py-5 px-4 md:px-8 text-gray-700 w-1/2 whitespace-pre-line">
                          {item.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex gap-3">
                  <span className="font-bold text-navy whitespace-nowrap">記載要領</span>
                  <span>所在地欄には、主たる営業所の所在地を記載すること。</span>
                </div>
                <div className="flex gap-3">
                  <span className="font-bold text-navy whitespace-nowrap">備考</span>
                  <div className="space-y-1">
                    <p>1　文字及び枠線の色彩は黒色、地の色彩は白色とする。</p>
                    <p>2　標識を営業所に掲示する場合には、用紙の大きさは、日本産業規格A4とする。</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Certificate Image */}
          <div className="mt-14 md:mt-20 flex justify-center w-full">
            <div className="rounded-2xl overflow-hidden border border-gray-200 bg-gray-50 p-4 md:p-8 w-full max-w-[900px]">
              <img
                src={certificateImg}
                alt="大阪府公安委員会 警備業認定証"
                className="w-full h-auto object-contain"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gray-50 py-16 md:py-24">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-navy mb-10">次のステップへ</h2>
          <div
            ref={ref3}
            className={`grid grid-cols-1 sm:grid-cols-2 gap-6 transition-all duration-700 ${
              v3 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-navy text-white font-bold text-base px-8 py-5 rounded-full transition-all duration-300 hover:bg-navy/90 hover:scale-105 whitespace-nowrap"
            >
              警備のご依頼・お見積もり
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
            <Link
              to="/recruit"
              className="inline-flex items-center justify-center gap-2 bg-gold text-navy font-bold text-base px-8 py-5 rounded-full transition-all duration-300 hover:bg-gold-light hover:scale-105 whitespace-nowrap"
            >
              採用情報・応募はこちら
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>
        </div>
      </section>

      <FaqSection items={companyFaq} bgColor="bg-white" />
    </div>
  );
}
